import type { RouterQuery } from './types';

/**
 * Merges React Router 7 route `params` and URL search params into the flat,
 * Next.js Pages Router-shaped `query` object that `AppRouter.query` consumers
 * expect.
 *
 * Parity rules with Next's `router.query`:
 * - A search key that appears once becomes a `string`; repeated keys become a
 *   `string[]` (e.g. `?tag=a&tag=b` -> `{ tag: ['a', 'b'] }`).
 * - Route params are surfaced as strings. `undefined` values (optional params
 *   that did not match) are omitted, matching Next's absence of the key.
 * - On a key collision the route param wins, since the path segment is the
 *   canonical source for that value.
 *
 * Note: catch-all/splat array shaping (Next's `[...slug]` -> `string[]`) is a
 * route-definition concern handled when the RR7 route tree is introduced; this
 * helper surfaces an RR7 splat under its raw param name as a string.
 */
export const mergeRouterQuery = (
  params: Readonly<Record<string, string | undefined>>,
  searchParams: URLSearchParams,
): RouterQuery => {
  const query: RouterQuery = {};

  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    query[key] = values.length > 1 ? values : values[0];
  }

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      query[key] = value;
    }
  }

  return query;
};
